import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Sparkles, Target } from 'lucide-react';
import VideoCard from '../components/VideoCard';
import VideoModal from '../components/VideoModal';
import { usePilatesStore } from '../store/pilatesStore';
import type { Video, Difficulty, MuscleGroup } from '../types';

const difficultyFilters: { value: Difficulty | 'all'; label: string; color: string }[] = [
  { value: 'all', label: 'All Levels', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'beginner', label: 'Beginner', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'intermediate', label: 'Intermediate', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'advanced', label: 'Advanced', color: 'bg-rose-100 text-rose-700 border-rose-200' }
];

const muscleGroupFilters: { value: MuscleGroup | 'all'; label: string; color: string }[] = [
  { value: 'all', label: 'All Areas', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'core', label: 'Core', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  { value: 'upper-body', label: 'Upper Body', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'lower-body', label: 'Lower Body', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'full-body', label: 'Full Body', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'glutes', label: 'Glutes', color: 'bg-orange-100 text-orange-700 border-orange-200' },
];

const categories = ['All', 'Morning', 'Flexibility', 'Back Health', 'Cardio', 'Toning', 'Balance', 'Advanced', 'Recovery'];

export default function Library() {
  const { videos, selectedDifficulty, setSelectedDifficulty, selectedMuscleGroup, setSelectedMuscleGroup } = usePilatesStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  const filteredVideos = videos
    .filter((video) => {
      const matchesDifficulty = selectedDifficulty === 'all' || video.difficulty === selectedDifficulty;
      const matchesCategory = selectedCategory === 'All' || video.category === selectedCategory;
      const matchesMuscleGroup = selectedMuscleGroup === 'all' || video.muscleGroup === selectedMuscleGroup;
      const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesDifficulty && matchesCategory && matchesMuscleGroup && matchesSearch;
    })
    .sort((a, b) => parseInt(a.id) - parseInt(b.id));

  const handleSchedule = (video: Video) => {
    // This will be handled by the calendar page
    setSelectedVideo(null);
    // Navigate to calendar with video pre-selected
    window.location.href = `/calendar?videoId=${video.id}`;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-rose-500 via-purple-500 to-indigo-500 text-white py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiAweiIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9Ii4xIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-30" />
        <div className="relative max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6"
          >
            <Sparkles className="w-4 h-4" />
            <span>Discover Your Perfect Workout</span>
          </motion.div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            Video Library
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto">
            Explore our curated collection of pilates workouts for every level
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search workouts, categories, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white border-2 border-gray-200 rounded-2xl focus:border-rose-300 focus:ring-0 focus:outline-none transition-colors text-gray-900 placeholder-gray-400"
            />
          </div>
        </motion.div>

        {/* Difficulty Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Difficulty Level</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {difficultyFilters.map((filter) => (
              <motion.button
                key={filter.value}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedDifficulty(filter.value)}
                className={`px-5 py-2 rounded-full font-medium border-2 transition-all ${
                  selectedDifficulty === filter.value
                    ? filter.color + ' shadow-md'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {filter.label}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Muscle Group Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Muscle Group</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {muscleGroupFilters.map((filter) => (
              <motion.button
                key={filter.value}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedMuscleGroup(filter.value)}
                className={`px-5 py-2 rounded-full font-medium border-2 transition-all ${
                  selectedMuscleGroup === filter.value
                    ? filter.color + ' shadow-md'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {filter.label}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Results Count */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredVideos.length}</span> workouts
          </p>
        </motion.div>

        {/* Video Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredVideos.map((video, index) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <VideoCard 
                video={video} 
                onSelect={setSelectedVideo}
                onCategoryClick={(category) => {
                  setSelectedCategory(category === selectedCategory ? 'All' : category);
                }}
                onMuscleGroupClick={(muscleGroup) => {
                  setSelectedMuscleGroup(muscleGroup === selectedMuscleGroup ? 'all' : muscleGroup);
                }}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Empty State */}
        {filteredVideos.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No workouts found</h3>
            <p className="text-gray-600">Try adjusting your filters or search query</p>
          </motion.div>
        )}
      </div>

      {/* Video Modal */}
      <VideoModal
        video={selectedVideo}
        onClose={() => setSelectedVideo(null)}
      />
    </div>
  );
}
